import { ILang, ILanguage, LanguageEnum } from './store/data'

export const layoutName = {
  frantiskovylazne: 'frantiskovylazne',
  spabookingCs: 'spabookingCs',
  spabookingDe: 'spabookingDe',
  spabookingDefault: 'spabookingDefault',
} as const

export type LayoutName = keyof typeof layoutName

export interface ILayoutConfig {
  colors: {
    primary: {
      highlight: string
      main: string
    }
    secondary: {
      highlight: string
      main: string
    }
    theme: {
      dark: string
      gradient: {
        from: string
        to: string
      }
      light: string
      main: string
      transparent: string
    }
  }
  copyright: string
  defaultAffiliatePartner?: string // Default affiliate email to apply if not specified in the query params
  destinationId: number | null
  favicon: 'frantiskovylazne' | 'spabooking'
  footer: {
    copy: Record<
      ILanguage,
      {
        contactDescription: string
        contactLink: string
        homepageLink: string
        imprintLink: string | null
        infoTitle: string
        legalTitle: string
        privacyLink: string
        sectionTitle: string
        termsLink: string
      }
    >
    directContact?: {
      email: string
      phone: string
    }
  }
  id: LayoutName
  instanceLanguage?: ILanguage
  instanceLanguageDomains?: Partial<Record<ILanguage, string>>
  links: {
    contact: Record<keyof ILang, string | null>
    gdpr: Record<keyof ILang, string | null>
    homepage: Record<keyof ILang, string | null>
    imprint?: Record<keyof ILang, string | null>
    tos: Record<keyof ILang, string | null>
  }
  logo: string
  logoHeight: number
  logoWidth: number
  supportedLocales: ILanguage[]
  title: string
}

const spabookingDefaultConfig: Omit<ILayoutConfig, 'id'> = {
  colors: {
    primary: {
      highlight: '#BEFFD4',
      main: '#00743C',
    },
    secondary: {
      highlight: '#793A00',
      main: '#FFCA0F',
    },
    theme: {
      dark: '#6f8c23',
      gradient: {
        from: '#7e9f26',
        to: '#a3c74d',
      },
      light: '#a8cb59',
      main: '#8fb42c',
      transparent: '#8FB42C19',
    },
  },
  copyright: 'SpaBooking.cz',
  defaultAffiliatePartner: 'info@spabooking.cz',
  destinationId: null,
  favicon: 'spabooking',
  footer: {
    copy: {
      cs: {
        contactDescription:
          'Potřebujete pomoc s výběrem pobytu nebo dokončením rezervace? Kontakt i podporu najdete na SpaBooking.',
        contactLink: 'Zákaznická podpora',
        homepageLink: 'Web SpaBooking',
        imprintLink: null,
        infoTitle: 'Informace',
        legalTitle: 'Právní informace',
        privacyLink: 'Ochrana soukromí',
        sectionTitle: 'Kontakt',
        termsLink: 'VOP',
      },
      de: {
        contactDescription:
          'Brauchen Sie Hilfe bei der Auswahl Ihres Aufenthalts oder beim Abschluss Ihrer Buchung? Kontakt und Unterstützung finden Sie bei SpaBooking.',
        contactLink: 'Kundenservice',
        homepageLink: 'SpaBooking Website',
        imprintLink: 'Impressum',
        infoTitle: 'Informationen',
        legalTitle: 'Rechtliches',
        privacyLink: 'Datenschutz',
        sectionTitle: 'Kontakt',
        termsLink: 'AGB',
      },
      en: {
        contactDescription:
          'Need help choosing your stay or completing a booking? Contact details and support are available on SpaBooking.',
        contactLink: 'Customer support',
        homepageLink: 'SpaBooking website',
        imprintLink: 'Imprint',
        infoTitle: 'Info',
        legalTitle: 'Legal',
        privacyLink: 'Privacy',
        sectionTitle: 'Contact',
        termsLink: 'T&C',
      },
      ru: {
        contactDescription:
          'Нужна помощь с выбором проживания или завершением бронирования? Контакты и поддержка доступны на SpaBooking.',
        contactLink: 'Служба поддержки',
        homepageLink: 'Сайт SpaBooking',
        imprintLink: 'Выходные данные',
        infoTitle: 'Информация',
        legalTitle: 'Правовая информация',
        privacyLink: 'Конфиденциальность',
        sectionTitle: 'Контакты',
        termsLink: 'Условия',
      },
    },
    directContact: {
      email: 'info@spabooking.de',
      phone: '+49 (0)351 83 80 41 44',
    },
  },
  links: {
    contact: {
      cs_CZ: 'https://spabooking.cz/pages/contact',
      de: 'https://spabooking.de/pages/contact',
      en: null,
      ru: null,
    },
    gdpr: {
      cs_CZ: 'https://spabooking.cz/pages/policies',
      de: 'https://spabooking.de/datenschutz',
      en: null,
      ru: null,
    },
    homepage: {
      cs_CZ: 'https://spabooking.cz/',
      de: 'https://spabooking.de/',
      en: null,
      ru: null,
    },
    imprint: {
      cs_CZ: null,
      de: 'https://spabooking.de/impressum',
      en: null,
      ru: null,
    },
    tos: {
      cs_CZ: 'https://spabooking.cz/pages/policies',
      de: 'https://spabooking.de/agb',
      en: null,
      ru: null,
    },
  },
  logo: '/logo/spabooking.png',
  logoHeight: 34,
  logoWidth: 170,
  supportedLocales: [LanguageEnum.CS, LanguageEnum.DE, LanguageEnum.EN],
  title: 'Spa Booking',
}

export const layoutConfig: Record<LayoutName, ILayoutConfig> = {
  frantiskovylazne: {
    colors: {
      primary: {
        highlight: '#BEFFD4',
        main: '#00743C',
      },
      secondary: {
        highlight: '#793A00',
        main: '#FFCA0F',
      },
      theme: {
        dark: '#A37719',
        gradient: {
          from: '#B5841C',
          to: '#AE7100',
        },
        light: '#CF9720',
        main: '#B37D11',
        transparent: '#B37D111A',
      },
    },
    copyright: 'Františkovy Lázně AQUAFORUM a.s.',
    destinationId: 1,
    favicon: 'frantiskovylazne',
    footer: {
      copy: {
        cs: {
          contactDescription:
            'Potřebujete pomoc s pobytem nebo rezervací? Kontakt i další podporu najdete na oficiálním webu Františkových Lázní.',
          contactLink: 'Zobrazit kontakty',
          homepageLink: 'Oficiální webové stránky',
          imprintLink: null,
          infoTitle: 'Informace',
          legalTitle: 'Právní informace',
          privacyLink: 'Ochrana soukromí',
          sectionTitle: 'Kontakt',
          termsLink: 'VOP',
        },
        de: {
          contactDescription:
            'Brauchen Sie Hilfe bei Ihrem Aufenthalt oder Ihrer Buchung? Kontakt und Unterstützung finden Sie auf der offiziellen Website von Františkovy Lázně.',
          contactLink: 'Kontakte ansehen',
          homepageLink: 'Offizielle Webseite',
          imprintLink: null,
          infoTitle: 'Informationen',
          legalTitle: 'Rechtliches',
          privacyLink: 'Datenschutz',
          sectionTitle: 'Kontakt',
          termsLink: 'AGB',
        },
        en: {
          contactDescription:
            'Need help with your stay or booking? Contact details and support are available on the official Frantiskovy Lazne website.',
          contactLink: 'View contacts',
          homepageLink: 'Official website',
          imprintLink: null,
          infoTitle: 'Info',
          legalTitle: 'Legal',
          privacyLink: 'Privacy',
          sectionTitle: 'Contact',
          termsLink: 'T&C',
        },
        ru: {
          contactDescription:
            'Нужна помощь с выбором проживания или бронированием? Контакты и поддержка доступны на официальном сайте Франтишковых Лазней.',
          contactLink: 'Посмотреть контакты',
          homepageLink: 'Официальный сайт',
          imprintLink: null,
          infoTitle: 'Информация',
          legalTitle: 'Правовая информация',
          privacyLink: 'Конфиденциальность',
          sectionTitle: 'Контакты',
          termsLink: 'Условия',
        },
      },
    },
    id: layoutName.frantiskovylazne,
    links: {
      contact: {
        cs_CZ: 'https://www.frantiskovylazne.cz/cs/kontakty',
        de: 'https://www.frantiskovylazne.cz/de/kontakt',
        en: 'https://www.frantiskovylazne.cz/en/contacts',
        ru: 'https://www.frantiskovylazne.cz/ru/kontakty',
      },
      gdpr: {
        cs_CZ: 'https://www.frantiskovylazne.cz/cs/zpracovani-osobnich-udaju',
        de: 'https://www.frantiskovylazne.cz/de/verarbeitung-personenbezogener-daten',
        en: 'https://www.frantiskovylazne.cz/en/personal-data-processing',
        ru: 'https://www.frantiskovylazne.cz/ru/obrabotka-personalnykh-dannykh',
      },
      homepage: {
        cs_CZ: 'https://www.frantiskovylazne.cz/cs',
        de: 'https://www.frantiskovylazne.cz/de',
        en: 'https://www.frantiskovylazne.cz/en',
        ru: 'https://www.frantiskovylazne.cz/ru',
      },
      tos: {
        cs_CZ: 'https://www.frantiskovylazne.cz/cs/vseobecne-obchodni-podminky',
        de: 'https://www.frantiskovylazne.cz/de/allgemeinen-vertragsbedingungen',
        en: 'https://www.frantiskovylazne.cz/en/general-terms-and-conditions',
        ru: 'https://www.frantiskovylazne.cz/ru/obshhie-kommercheskie-uslovija',
      },
    },
    logo: '/logo/frantisek.svg',
    logoHeight: 132,
    logoWidth: 64,
    supportedLocales: [LanguageEnum.CS, LanguageEnum.DE, LanguageEnum.EN],
    title: 'Františkovy Lázně AQUAFORUM a.s.',
  },
  spabookingCs: {
    ...spabookingDefaultConfig,
    id: layoutName.spabookingCs,
    instanceLanguage: LanguageEnum.CS,
    instanceLanguageDomains: {
      [LanguageEnum.CS]: 'https://rezervace.spabooking.cz/',
      [LanguageEnum.DE]: 'https://reservierung.spabooking.de/',
    },
    supportedLocales: [LanguageEnum.CS, LanguageEnum.DE],
  },
  spabookingDe: {
    ...spabookingDefaultConfig,
    copyright: 'SpaBooking.de',
    id: layoutName.spabookingDe,
    instanceLanguage: LanguageEnum.DE,
    instanceLanguageDomains: {
      [LanguageEnum.CS]: 'https://rezervace.spabooking.cz/',
      [LanguageEnum.DE]: 'https://reservierung.spabooking.de/',
    },
    supportedLocales: [LanguageEnum.CS, LanguageEnum.DE],
  },
  spabookingDefault: {
    ...spabookingDefaultConfig,
    id: layoutName.spabookingDefault,
  },
}

export const getLayoutConfig = (): ILayoutConfig => {
  try {
    const layout = process.env.NEXT_PUBLIC_LAYOUT_NAME as LayoutName

    if (!layoutConfig[layout]) {
      console.warn(`Layout "${layout}" not found, using default`)
      return layoutConfig[layoutName.spabookingDefault]
    }

    return layoutConfig[layout]
  } catch (error) {
    console.warn('Error getting layout config, using default:', error)
    return layoutConfig[layoutName.spabookingDefault]
  }
}
